import { useState, useRef } from "react";
import { Form, DatePicker } from "antd";
import { v4 as uuidv4 } from "uuid";
import { clock } from "../../../../utils/clock";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";

/**
 * useCoreState
 *
 * Centralizes the large block of useState / useRef declarations from PointOfsaleCore.
 * Pure state initialization — no effects, no handlers.
 */
const useCoreState = ({ tillData, defaultCustomer, userData, tillValue, tillDocumentSequence, posConfig }) => {
  const { RangePicker } = DatePicker;

  // Clock
  const [displayClock, setDisplayClock] = useState(clock());

  // Keyboard
  const [orderHistoryInput, setOrderHistoryInput] = useState("");
  const [layoutName, setLayoutName] = useState("default");
  const [searchTerm, setSearchTerm] = useState("");
  const [salesRepValue, setSalesRepValue] = useState("");

  // Loading / UI
  const [loading, setLoading] = useState(false);
  // const [detailsLoading, setDetailsLoading] = useState(false);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [filtersFlag, setFiltersFlag] = useState(false);
  const [filteredDate, setFilterdDate] = useState(null);
  const [searchHistoryInput, setSearchhistoryInput] = useState("");
  const [notesValue, setNotesValue] = useState("");
  const [exchangeNotesValue, setExchangeNotesValue] = useState("");
  const [activeCartTable, setActiveCartTable] = useState("");
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedProductForNotes, setSelectedProductForNotes] = useState(null);
  const [open, setOpen] = useState(false);

  // Product / stock
  const [productStock, setProductStock] = useState(false);
  const [showCompleteSaleStock, setShowCompleteSaleStock] = useState(false);
  const [completeSaleStockData, setCompleteSaleStockData] = useState([]);
  const [stockList, setStockList] = useState([]);
  const [productBOMStock, setProductBOMStock] = useState(false);
  const [productBOMStockData, setProductBOMStockData] = useState([]);
  const [saleTypeProducts, setSaleTypeProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const [productSearchInput, setProductSearchInput] = useState("");
  const [productsCopy, setProductsCopy] = useState([]);
  // NOTE: productsData / setProductsData removed from here — they were
  // duplicated in useProductOperations.js. The search path wrote to
  // useProductOperations's copy while components read this empty one,
  // causing the multi-product popup to show blank in pre-order. (Issue #24)

  // Currency
  const [currencyType, setCurrencyType] = useState({
    currSymbolLeft: "₹",
    currSymbolRight: "",
    stdPrecision: 2,
  });
  const [currenciesData, setCurrenciesData] = useState([...(tillData?.tillAccess?.csBunit?.currencies[0]?.denominations || [])].sort((a, b) => b.value - a.value));

  // Document type
  const [selectedDocumentType, setSelectedDocumentType] = useState("Invoice");
  const [showDocumentPopup, setShowDocumentPopup] = useState(false);

  // Input values (customer form keyboard)
  const [currentInput, setCurrentInput] = useState(null);
  const [tableCards, setTableCards] = useState([]);
  const [inputValues, setInputValues] = useState({
    name: "",
    lastName: "",
    email: "",
    mobile: "",
    pincode: "",
    street: "",
    city: "",
    pincode1: "",
    taxID: "",
    cardNumber: "",
  });
  const [keyboardInputFields, setKeyboardInputFields] = useState({});

  // Payment
  const [keyValue, setKeyValue] = useState("0");
  const [isCardPaymentLoading, setIsCardPaymentLoading] = useState(false);
  const [cardPaymnetError, setCardPaymnetError] = useState(false);
  const [cardPaymnetStatus, setCardPaymnetStatus] = useState(false);
  const [amount, setAmount] = useState("");
  const [overPayedAmount, setOverPayedAmount] = useState(0);
  const [paytmQrCodeModalOpens, setPaytmQrCodeModalOpens] = useState(false);
  const [qrCodeResponse, setQrCodeResponse] = useState({});
  const [cardPaymnetStatusPLB, setCardPaymnetStatusPLB] = useState(false);
  const [showCardPaymnteModal, setShowCardPaymnteModal] = useState(false);
  const [integratedPaymentStatus, setIntegratedPaymentStatus] = useState({
    status: "",
    message: "",
    txnId: "",
    paymentMode: "",
    amount: "",
  });

  // Metadata
  const [metaData, setMetaData] = useState([]);
  const [saleType, setSaleType] = useState("");

  // Custom mix
  const [showCustomMix, setShowCustomMix] = useState(false);
  const [options, setOptions] = useState([]);
  const [customMixItems, setCustomMixItems] = useState([]);
  const [selectedCustomMix, setSelectedCustomMix] = useState([]);
  const [groupQty, setGroupQty] = useState(1);
  const [existingGroupItems, setExistingGroupItems] = useState(true);

  // Batch / weight / stock modal
  const [displayBatchSelection, setDisplayBatchSelection] = useState(false);
  const [batchSetAvailable, setBatchSetAvailable] = useState([]);
  const [isStockModal, setIsStockModal] = useState(false);
  const [tempCart, setTempCart] = useState({});
  const [displayManualQtyWeightInput, setDisplayManualQtyWeightInput] = useState(false);

  // Management screen
  const [managementScreenShow, setManagementScreenShow] = useState(false);
  const [sideMenuDrawervisible, setSideMenuDrawervisible] = useState(false);

  // Sales rep
  const [salesRepresent, setSalesRepresent] = useState({});
  const [salesRepresentDefaultLine, setSalesRepresentDefaultLine] = useState({ salesRepresentId: null, name: null });
  const [numb, setNumb] = useState(0);

  // Coupon
  const [selectedExternalCouponType, setSelectedExternalCouponTypeValue] = useState("");

  // Order
  const [orderTypeSelection, setOrderTypeSelection] = useState("Dine In");
  const [loader, setLoader] = useState(false);
  const [isSaleTypeFlag, setIsSaleTypeFlag] = useState(false);
  const [displayAddOnSelection, setDisplayAddOnSelection] = useState(false);
  const [showSaleType, setShowSaleType] = useState(() => (posConfig?.enableSaleType === "Y" ? true : false));
  const [isCardPaymentFlag, setIsCardPaymentFlag] = useState(false);
  const [editFlag, setEditFlag] = useState(false);

  // Sales history
  const [salesHistoryCustomerSearchInput, setSalesHistoryCustomerSearchInput] = useState("");
  const [salesHistoryDocumentNoSearchInput, setSalesHistoryDocumentNoSearchInput] = useState("");

  // Sync / misc
  const [syncLoad, setSyncLoad] = useState(false);
  const [selectedItemsModalOpen, setSelectedItemsModalOpen] = useState(false);
  const [isParkedBill, setIsparkedBill] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [dateRange, setDateRange] = useState([]);
  const [isPopoverVisible, setPopoverVisible] = useState(false);
  const [showCouponOtpPopup, setShowCouponOtpPopup] = useState(false);
  const [couponOtp, setCouponOtp] = useState(Array(6).fill(""));
  const [couponOtpError, setCouponOtpError] = useState(false);
  const [couponResendTimer, setCouponResendTimer] = useState(0);
  const [couponCanResend, setCouponCanResend] = useState(true);
  const [verificationCouponStatus, setVerificationCouponStatus] = useState(false);

  // Call center
  const [callCenterVisible, setCallCenterVisible] = useState(false);
  const [isCallCenterFlow, setIsCallCenterFlow] = useState(false);
  const [showCallCenterPage, setShowCallCenterPage] = useState(false);
  const [selectedUser, setSelectedUser] = useState();
  const [holdQueue, setHoldQueue] = useState(() => {
    const saved = localStorage.getItem("callCenterHoldQueue");
    return saved ? JSON.parse(saved) : [];
  });

  // Addons
  const [showAddonsCustomizePopup, setShowAddonsCustomizePopUp] = useState(false);
  const [addonSourceItem, setAddonSourceItem] = useState(null);
  const [addonRepeatDisabled, setAddonRepeatDisabled] = useState(false);
  const [notesPopup, setNotesPopup] = useState(false);
  const [rejectQuotation, setRejectQuotation] = useState(false);
  const [modalEditState, setModalEditState] = useState(false);
  const [paymentType, setPaymentType] = useState("edc");
  const [dynamicForm, setDynamicForm] = useState(false);

  // Order type
  const [orderType, setOrderType] = useState();
  const [posSaleTypes, setPosSaleTypes] = useState([]);
  const [displaySetOrderType, setDisplayOrderType] = useState(false);

  // Customer
  const [properties, setProperties] = useState("");
  const [orderTimeDetails, setOrderTimeDetails] = useState({ orderStartTime: "", orderEndTime: "", paymentStartTime: "" });

  // Exchange cart
  const [exchangeCart, setExchangeCart] = useState(useCartStore.getState().exchangeCart || {});
  const [splitCarts, setSplitCarts] = useState([]);
  const [cartObj, setOrderObj] = useState(null);

  // Sale type
  const [selectedSaleType, setSelectedSaleType] = useState(() => {
    const std = usePOSConfigStore.getState().saleTypeData;
    if (std) {
      return std?.cwrSaletype?.name;
    }
    return "";
  });
  const [selectedSaleTypeData, setSelectedSaleTypeData] = useState(() => {
    const std = usePOSConfigStore.getState().saleTypeData;
    if (std) {
      return std;
    }
    return "";
  });

  // Document sequence
  const [documentSequence, setDocumnetSequence] = useState(tillDocumentSequence);

  // Forms
  const [form] = Form.useForm();
  const [guestForm] = Form.useForm();
  const [additionalInfoForm] = Form.useForm();
  const [CardPaymentForm] = Form.useForm();
  const [dynamicModalForm] = Form.useForm();

  // Refs
  const dateRef = useRef(null);
  const notesInputRef = useRef();
  const returnProcessRef = useRef();
  const paymentModalInputRef = useRef();
  const quantityInputRef = useRef();
  const metaDataRef = useRef([]);
  const redeemNumberRef = useRef(null);
  const loyaltyInputRef = useRef(null);
  const productSearchInputRef = useRef(null);
  const giftCardRef = useRef(null);
  // salesRepRef is provided by useSalesRepresentative hook
  const keyboardRef = useRef(null);
  const searchInputRef = useRef(null);
  const qtyInputRef = useRef(null);
  const keyboard = useRef(null);
  const [activeInput, setActiveInput] = useState("search");

  // Cross-hook data refs
  const giftCardDataRef = useRef([]);
  const selectedPaymentMethodRef = useRef({});
  const addProductRef = useRef(null);
  const posLogActivityRef = useRef(null);
  const fetchOnlineBatchesRef = useRef(null);
  const hasShownCustomerModalRef = useRef(false);
  const cartRef = useRef();
  const sendWebHookDataRef = useRef(null);
  const handleLoyaltyPaymentRef = useRef(null);
  const handleDeleteLoyaltyPaymentRef = useRef(null);
  const handleSelectedSaleTypeForProductsRef = useRef(null);
  const processBillDiscountsRef = useRef(null);
  const setOpenSalesRepRef = useRef(null);
  const verifyStockRef = useRef(null);
  const handleWalletPostPaymentRef = useRef(null);
  const handleGiftCardTopupPostPaymentRef = useRef(null);
  const showCustomerWalletRef = useRef(false);
  const showGiftCardTopupRef = useRef(false);

  // Refs object for customer form
  const refs = {
    name: useRef(null),
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    mobile: useRef(null),
    pincode: useRef(null),
    street: useRef(null),
    city: useRef(null),
    pincode1: useRef(null),
    taxID: useRef(null),
    cardNumber: useRef(null),
  };

  // IRN (B2B e-Invoice) state
  const [showIRNModal, setShowIRNModal] = useState(false);
  const [irnStatus, setIrnStatus] = useState("PENDING");
  const [irnNumber, setIrnNumber] = useState("");
  const [irnPdfUrl, setIrnPdfUrl] = useState("");
  const [irnLoading, setIrnLoading] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState(null);
  const [invoiceProcessing, setInvoiceProcessing] = useState("PENDING");
  const [invoicePrinted, setInvoicePrinted] = useState("PENDING");
  const [irnGenerating, setIrnGenerating] = useState("PENDING");
  const [irnDownloadReady, setIrnDownloadReady] = useState("PENDING");
  const [pendingIRNOrderId, setPendingIRNOrderId] = useState(null);
  const [irnFailureMessage, setIrnFailureMessage] = useState("");

  return {
    RangePicker,
    displayClock,
    setDisplayClock,
    orderHistoryInput,
    setOrderHistoryInput,
    layoutName,
    setLayoutName,
    searchTerm,
    setSearchTerm,
    salesRepValue,
    setSalesRepValue,
    loading,
    setLoading,
    // detailsLoading,
    // setDetailsLoading,
    filterDrawer,
    setFilterDrawer,
    filtersFlag,
    setFiltersFlag,
    filteredDate,
    setFilterdDate,
    searchHistoryInput,
    setSearchhistoryInput,
    notesValue,
    setNotesValue,
    exchangeNotesValue,
    setExchangeNotesValue,
    activeCartTable,
    setActiveCartTable,
    calendarVisible,
    setCalendarVisible,
    selectedDate,
    setSelectedDate,
    selectedProductForNotes,
    setSelectedProductForNotes,
    open,
    setOpen,
    productStock,
    setProductStock,
    showCompleteSaleStock,
    setShowCompleteSaleStock,
    completeSaleStockData,
    setCompleteSaleStockData,
    stockList,
    setStockList,
    productBOMStock,
    setProductBOMStock,
    productBOMStockData,
    setProductBOMStockData,
    saleTypeProducts,
    setSaleTypeProducts,
    showProducts,
    setShowProducts,
    productSearchInput,
    setProductSearchInput,
    productsCopy,
    setProductsCopy,
    // productsData/setProductsData come from useProductOperations now
    currencyType,
    setCurrencyType,
    currenciesData,
    setCurrenciesData,
    selectedDocumentType,
    setSelectedDocumentType,
    showDocumentPopup,
    setShowDocumentPopup,
    currentInput,
    setCurrentInput,
    tableCards,
    setTableCards,
    inputValues,
    setInputValues,
    keyboardInputFields,
    setKeyboardInputFields,
    keyValue,
    setKeyValue,
    isCardPaymentLoading,
    setIsCardPaymentLoading,
    cardPaymnetError,
    setCardPaymnetError,
    cardPaymnetStatus,
    setCardPaymnetStatus,
    amount,
    setAmount,
    overPayedAmount,
    setOverPayedAmount,
    paytmQrCodeModalOpens,
    setPaytmQrCodeModalOpens,
    qrCodeResponse,
    setQrCodeResponse,
    cardPaymnetStatusPLB,
    setCardPaymnetStatusPLB,
    showCardPaymnteModal,
    setShowCardPaymnteModal,
    integratedPaymentStatus,
    setIntegratedPaymentStatus,
    metaData,
    setMetaData,
    saleType,
    setSaleType,
    showCustomMix,
    setShowCustomMix,
    options,
    setOptions,
    customMixItems,
    setCustomMixItems,
    selectedCustomMix,
    setSelectedCustomMix,
    groupQty,
    setGroupQty,
    existingGroupItems,
    setExistingGroupItems,
    displayBatchSelection,
    setDisplayBatchSelection,
    batchSetAvailable,
    setBatchSetAvailable,
    isStockModal,
    setIsStockModal,
    tempCart,
    setTempCart,
    displayManualQtyWeightInput,
    setDisplayManualQtyWeightInput,
    managementScreenShow,
    setManagementScreenShow,
    sideMenuDrawervisible,
    setSideMenuDrawervisible,
    salesRepresent,
    setSalesRepresent,
    salesRepresentDefaultLine,
    setSalesRepresentDefaultLine,
    numb,
    setNumb,
    selectedExternalCouponType,
    setSelectedExternalCouponTypeValue,
    orderTypeSelection,
    setOrderTypeSelection,
    loader,
    setLoader,
    isSaleTypeFlag,
    setIsSaleTypeFlag,
    displayAddOnSelection,
    setDisplayAddOnSelection,
    showSaleType,
    setShowSaleType,
    isCardPaymentFlag,
    setIsCardPaymentFlag,
    editFlag,
    setEditFlag,
    salesHistoryCustomerSearchInput,
    setSalesHistoryCustomerSearchInput,
    salesHistoryDocumentNoSearchInput,
    setSalesHistoryDocumentNoSearchInput,
    syncLoad,
    setSyncLoad,
    selectedItemsModalOpen,
    setSelectedItemsModalOpen,
    isParkedBill,
    setIsparkedBill,
    isOnline,
    setIsOnline,
    dateRange,
    setDateRange,
    isPopoverVisible,
    setPopoverVisible,
    showCouponOtpPopup,
    setShowCouponOtpPopup,
    couponOtp,
    setCouponOtp,
    couponOtpError,
    setCouponOtpError,
    couponResendTimer,
    setCouponResendTimer,
    couponCanResend,
    setCouponCanResend,
    verificationCouponStatus,
    setVerificationCouponStatus,
    callCenterVisible,
    setCallCenterVisible,
    isCallCenterFlow,
    setIsCallCenterFlow,
    showCallCenterPage,
    setShowCallCenterPage,
    selectedUser,
    setSelectedUser,
    holdQueue,
    setHoldQueue,
    showAddonsCustomizePopup,
    setShowAddonsCustomizePopUp,
    addonSourceItem,
    setAddonSourceItem,
    addonRepeatDisabled,
    setAddonRepeatDisabled,
    notesPopup,
    setNotesPopup,
    rejectQuotation,
    setRejectQuotation,
    modalEditState,
    setModalEditState,
    paymentType,
    setPaymentType,
    dynamicForm,
    setDynamicForm,
    orderType,
    setOrderType,
    posSaleTypes,
    setPosSaleTypes,
    displaySetOrderType,
    setDisplayOrderType,
    properties,
    setProperties,
    orderTimeDetails,
    setOrderTimeDetails,
    exchangeCart,
    setExchangeCart,
    splitCarts,
    setSplitCarts,
    cartObj,
    setOrderObj,
    selectedSaleType,
    setSelectedSaleType,
    selectedSaleTypeData,
    setSelectedSaleTypeData,
    documentSequence,
    setDocumnetSequence,
    form,
    guestForm,
    additionalInfoForm,
    CardPaymentForm,
    dynamicModalForm,
    dateRef,
    notesInputRef,
    returnProcessRef,
    paymentModalInputRef,
    quantityInputRef,
    metaDataRef,
    redeemNumberRef,
    loyaltyInputRef,
    productSearchInputRef,
    giftCardRef,
    keyboardRef,
    searchInputRef,
    qtyInputRef,
    keyboard,
    activeInput,
    setActiveInput,
    giftCardDataRef,
    selectedPaymentMethodRef,
    addProductRef,
    posLogActivityRef,
    fetchOnlineBatchesRef,
    hasShownCustomerModalRef,
    cartRef,
    sendWebHookDataRef,
    handleLoyaltyPaymentRef,
    handleDeleteLoyaltyPaymentRef,
    handleSelectedSaleTypeForProductsRef,
    processBillDiscountsRef,
    setOpenSalesRepRef,
    verifyStockRef,
    handleWalletPostPaymentRef,
    handleGiftCardTopupPostPaymentRef,
    showCustomerWalletRef,
    showGiftCardTopupRef,
    refs,
    // IRN (B2B e-Invoice) state
    showIRNModal,
    setShowIRNModal,
    irnStatus,
    setIrnStatus,
    irnNumber,
    setIrnNumber,
    irnPdfUrl,
    setIrnPdfUrl,
    irnLoading,
    setIrnLoading,
    completedOrderId,
    setCompletedOrderId,
    invoiceProcessing,
    setInvoiceProcessing,
    invoicePrinted,
    setInvoicePrinted,
    irnGenerating,
    setIrnGenerating,
    irnDownloadReady,
    setIrnDownloadReady,
    pendingIRNOrderId,
    setPendingIRNOrderId,
    irnFailureMessage,
    setIrnFailureMessage,
  };
};

export default useCoreState;
